import {
  ICohortFieldsFragment,
  ICohortRefFieldsFragment,
  IPercentileFilterFieldsFragment,
  IPercentileFilterInput,
  Maybe,
} from '../../apollo/graphqlSchema';
import { ICohortRow } from '../../../redux/slices/oldBuilder';
import { defaultCohortRow } from '../../../redux/slices/oldBuilder/defaults';
import { parseMeasureToCohortRow } from '../measures/parseMeasureToCohortRow';
import {
  defaultCohortDurationStart,
  defaultLookForwardWindow,
  parseCohortDurationStartDate,
  parseFunnelLookForwardWindow,
  parseRetentionLookForwardWindow,
} from '../../../redux/slices/oldBuilder/utils';

const parseCohortPercentileFilter = (
  percentileFilter: Maybe<IPercentileFilterFieldsFragment>,
): Maybe<IPercentileFilterInput> => {
  if (!percentileFilter) {
    return null;
  }

  return {
    values: percentileFilter.values,
    operator: percentileFilter.operator.key,
  };
};

export const parseCohortToBuilder = (
  cohort: ICohortFieldsFragment,
): Partial<ICohortRow> => {
  const result: Partial<ICohortRow> = {
    isActive: true,
  };

  if (cohort.__typename === 'CompoundCohort') {
    const { userAlias, version } = cohort;
    // Named compound cohort case
    if (version?.versionId) {
      return {
        ...result,
        userAliasId: userAlias?.aliasId,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
        isCompoundCohort: true,
      };
    }
  }

  const { subject, datePicker } =
    cohort.__typename === 'CompoundCohort' // XXX Compound cohorts don't have subject/start and end dates
      ? { subject: null, datePicker: null }
      : cohort;

  result.subject = subject?.key ?? null;
  result.datePicker = datePicker;

  // default values for cohort duration start / look forward window
  result.cohortDurationStart = defaultCohortDurationStart;
  result.lookForwardWindow = defaultLookForwardWindow;

  if (cohort.__typename === 'ExpressionCohort') {
    const {
      type,
      filter,
      timeWindow,
      timeWindowValues,
      version,
      analysis,
      operator,
      operatorValues,
      percentileFilter,
      measureId,
    } = cohort;

    const output = {
      ...result,
      simpleCohortTypeKey: type.key,
      conditionFilter: filter,
      timeWindow: timeWindow.key,
      timeWindowValues,
      analysisId: analysis.id,
      measureId,
      operator: operator.expressionKey,
      operatorValues,
      percentileFilter: parseCohortPercentileFilter(percentileFilter),
      aliasReplacements: analysis.pref.aliasReplacements,
    };

    // Named simple expression cohort
    if (version?.versionId) {
      return {
        ...output,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
      };
    }

    // simple expression cohort
    return output;
  }

  if (cohort.__typename === 'FunnelCohort') {
    const {
      type,
      filter,
      timeWindow,
      timeWindowValues,
      version,
      analysis,
      operator,
      funnelOperatorValues,
      lookForwardWindow,
      durationStartDate,
      timeToConvert,
    } = cohort;

    const output: Partial<ICohortRow> = {
      ...result,
      simpleCohortTypeKey: type.key,
      conditionFilter: filter,
      timeWindow: timeWindow.key,
      timeWindowValues,
      analysisId: analysis.id,
      operator: operator.funnelKey,
      operatorValues: funnelOperatorValues,
      lookForwardWindow: parseFunnelLookForwardWindow(
        analysis.query,
        lookForwardWindow,
      ),
      cohortDurationStart: parseCohortDurationStartDate(
        analysis.query,
        durationStartDate,
      ),
      aliasReplacements: analysis.pref.aliasReplacements,
      timeToConvert: timeToConvert
        ? {
            operator: timeToConvert.operator.key,
            operatorValues: timeToConvert.values,
            timeUnit: timeToConvert.timeUnit,
          }
        : null,
    };

    // Named simple funnel cohort
    if (version?.versionId) {
      return {
        ...output,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
      };
    }

    // simple funnel cohort
    return output;
  }

  if (cohort.__typename === 'MeasureCohort') {
    const {
      type,
      filter,
      timeWindow,
      timeWindowValues,
      version,
      operator,
      operatorValues,
      percentileFilter,
      measure,
    } = cohort;

    const output = {
      ...result,
      ...parseMeasureToCohortRow(measure),
      simpleCohortTypeKey: type.key,
      conditionFilter: filter,
      timeWindow: timeWindow.key,
      timeWindowValues,
      operator: operator.measureKey,
      operatorValues,
      percentileFilter: parseCohortPercentileFilter(percentileFilter),
    };

    // Named simple measure cohort
    if (version?.versionId) {
      return {
        ...output,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
      };
    }

    // simple measure cohort
    return output;
  }

  if (cohort.__typename === 'RetentionCohort') {
    const {
      type,
      filter,
      timeWindow,
      timeWindowValues,
      version,
      analysis,
      operator,
      retentionOperatorValues,
      returnedOperator,
      durationStartDate,
      lookForwardWindow,
    } = cohort;

    const output: Partial<ICohortRow> = {
      ...result,
      simpleCohortTypeKey: type.key,
      conditionFilter: filter,
      timeWindow: timeWindow.key,
      timeWindowValues,
      analysisId: analysis.id,
      operator: operator.retentionKey,
      operatorValues: retentionOperatorValues,
      returnedOperator: returnedOperator?.key ?? '',
      cohortDurationStart: parseCohortDurationStartDate(
        analysis.query,
        durationStartDate,
      ),
      lookForwardWindow: parseRetentionLookForwardWindow(
        analysis.query.retentionUnit,
        lookForwardWindow,
      ),
    };

    // Named simple retention cohort
    if (version?.versionId) {
      return {
        ...output,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
      };
    }

    // simple retention cohort
    return output;
  }

  if (cohort.__typename === 'PerformedCohort') {
    const {
      type,
      filter,
      timeWindow,
      timeWindowValues,
      version,
      operator,
      performedOperatorValues,
      eventConditions,
    } = cohort;

    const output = {
      ...result,
      simpleCohortTypeKey: type.key,
      conditionFilter: filter,
      timeWindow: timeWindow.key,
      timeWindowValues,
      operator: operator.performedKey,
      operatorValues: performedOperatorValues,
      eventConditions,
    };

    // Named simple performed cohort
    if (version?.versionId) {
      return {
        ...output,
        cohortId: version.versionId,
        showCohort: true,
        defaultNamedCohort: cohort,
      };
    }

    // simple performed cohort
    return output;
  }

  return result;
};

export const parseCohortRefToBuilder = (
  cohortRef: ICohortRefFieldsFragment,
): ICohortRow => {
  const { cohort, props } = cohortRef;

  const result: ICohortRow = {
    ...defaultCohortRow(),
    ...parseCohortToBuilder(cohort),
  };

  result.contextInput.samplePercentage = props.samplePercentage;
  result.contextInput.dateRange = props.dateRange;

  return result;
};
